import React, { useState } from 'react';
import { NtIcon } from '../NotionUI';
import { useLanguage } from '../../i18n/LanguageContext';

export const COVERS = ['c1', 'c2', 'c3', 'c4', 'c5', 'c6', 'c7', 'c8'];

const EMOJIS = [
  '📋', '📌', '🎯', '🚀', '💡', '🧩', '📈', '📊', '🗓️', '🗂️', '📁', '📝',
  '✅', '⭐', '🔥', '🛠️', '🔧', '🧪', '🎨', '🎬', '📣', '📱', '💻', '🖥️',
  '🛒', '📦', '🚚', '🏢', '🏠', '💼', '🤝', '👥', '🎓', '📚', '🔍', '🔒',
  '💰', '🧾', '🎟️', '🎁', '🌱', '☀️', '🌍', '⚡', '🐞', '🎧', '🪪', '🗺️',
];

/**
 * Intestazione di un progetto: copertina, icona (emoji) scelta da un elenco,
 * nome e descrizione modificabili, e menu "altro" (duplica, elimina).
 */
function ProjectHeader({ project, onSave, onDuplicate, onDelete, onExport, onImport, onTrash, onToggleFavorite }) {
  const { t } = useLanguage();
  const [menu, setMenu] = useState(null); // 'icon' | 'cover' | 'more'
  const close = () => setMenu(null);

  return (
    <>
      {project.cover && <div className={`pd-cover pd-cover-${project.cover}`} aria-hidden="true" />}
      <div className="pd-header-tools">
        <span className="pv-tab-wrap">
          <button type="button" className="nt-ghost-btn pd-tool-small" onClick={() => setMenu('cover')}>
            <NtIcon name="layout" /> {project.cover ? t('projectDetail.changeCover') : t('projectDetail.addCover')}
          </button>
          {menu === 'cover' && (
            <>
              <div className="pd-overlay" onClick={close} />
              <div className="pd-popover pd-cover-picker">
                <div className="pd-cover-swatches">
                  {COVERS.map((c) => (
                    <button key={c} type="button" className={`pd-cover-swatch pd-cover-${c}`} aria-label={t('projectDetail.coverOption', { name: c })} onClick={() => { close(); onSave({ cover: c }); }} />
                  ))}
                </div>
                {project.cover && <button type="button" className="pv-menu-item" onClick={() => { close(); onSave({ cover: '' }); }}><NtIcon name="trash" /> {t('projectDetail.removeCover')}</button>}
              </div>
            </>
          )}
        </span>
        <span className="pd-header-right">
          <button type="button" className={'nt-ghost-btn pd-tool-small pd-star' + (project.is_favorite ? ' pd-star-on' : '')} onClick={onToggleFavorite} aria-label={project.is_favorite ? t('projects.removeFavorite') : t('projects.addFavorite')} title={project.is_favorite ? t('projects.removeFavorite') : t('projects.addFavorite')}>
            {project.is_favorite ? '★' : '☆'}
          </button>
          <button type="button" className="nt-ghost-btn pd-tool-small" onClick={() => window.dispatchEvent(new Event('open-global-search'))} title="Ctrl+K">
            <NtIcon name="search" /> {t('projects.search')}
          </button>
        </span>
        <span className="pv-tab-wrap pd-more">
          <button type="button" className="nt-ghost-btn pd-tool-small" onClick={() => setMenu('more')} aria-label={t('projectDetail.moreActions')} title={t('projectDetail.moreActions')}>
            <NtIcon name="more" />
          </button>
          {menu === 'more' && (
            <>
              <div className="pd-overlay" onClick={close} />
              <div className="pd-popover pv-menu pd-more-menu">
                <button type="button" className="pv-menu-item" onClick={() => { close(); onDuplicate(); }}><NtIcon name="copy" /> {t('projectDetail.duplicateProject')}</button>
                <button type="button" className="pv-menu-item" onClick={() => { close(); onExport(); }}><NtIcon name="arrowDown" /> {t('projectCsv.export')}</button>
                <button type="button" className="pv-menu-item" onClick={() => { close(); onImport(); }}><NtIcon name="arrowUp" /> {t('projectCsv.importMenu')}</button>
                <button type="button" className="pv-menu-item" onClick={() => { close(); onTrash(); }}><NtIcon name="trash" /> {t('projectDetail.trash')}</button>
                <button type="button" className="pv-menu-item" onClick={() => { close(); onDelete(); }}><NtIcon name="trash" /> {t('projectDetail.deleteProject')}</button>
              </div>
            </>
          )}
        </span>
      </div>

      <div className="nt-heading pd-heading">
        <span className="pv-tab-wrap pd-icon-wrap">
          <button type="button" className="pd-heading-icon pd-icon-btn" onClick={() => setMenu('icon')} aria-label={t('projectDetail.changeIcon')} title={t('projectDetail.changeIcon')}>
            {project.icon || '📋'}
          </button>
          {menu === 'icon' && (
            <>
              <div className="pd-overlay" onClick={close} />
              <div className="pd-popover pd-emoji-picker">
                <div className="pd-emoji-grid">
                  {EMOJIS.map((emoji) => (
                    <button key={emoji} type="button" className="pd-emoji" onClick={() => { close(); onSave({ icon: emoji }); }}>{emoji}</button>
                  ))}
                </div>
                {project.icon && <button type="button" className="pv-menu-item" onClick={() => { close(); onSave({ icon: '' }); }}><NtIcon name="trash" /> {t('projectDetail.removeIcon')}</button>}
              </div>
            </>
          )}
        </span>
        <div className="pd-heading-text">
          <input
            key={`name:${project.id}`}
            className="pd-title"
            defaultValue={project.name}
            aria-label={t('projectDetail.projectName')}
            maxLength={190}
            onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
            onBlur={(e) => {
              const name = e.target.value.trim();
              if (!name) e.target.value = project.name;
              else if (name !== project.name) onSave({ name });
            }}
          />
          <input
            key={`desc:${project.id}`}
            className="pd-description"
            defaultValue={project.description || ''}
            placeholder={t('projectDetail.descriptionPlaceholder')}
            aria-label={t('projectDetail.description')}
            onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
            onBlur={(e) => e.target.value.trim() !== (project.description || '') && onSave({ description: e.target.value.trim() })}
          />
        </div>
      </div>
    </>
  );
}

export default ProjectHeader;
